import { useState, useEffect, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { groupByName } from "../lib/variants";
import { GOALS, goalBySlug, familiesForGoal } from "../lib/goals";
import { useCart } from "../context/CartContext";
import { Seo, siteOrigin } from "../components/Seo";
import { ProductGrid } from "../components/ProductCard";
import { GoalTile } from "../components/GoalTile";
import { SORTS } from "./Shop";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function GuideLinks({ guides, goal }) {
  if (!guides.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-5" data-testid="category-guide-links">
      {guides.map((g) => (
        <Link
          key={g.slug}
          to={`/guides/${g.slug}`}
          data-testid={`category-guide-link-${g.slug}`}
          className="inline-flex items-center gap-1.5 bg-white border border-[#DDD7CF] rounded-none px-3.5 py-1.5 text-xs font-semibold text-[#1A1A1A] hover:border-[#8C7B6B] transition-colors"
          style={{ borderColor: undefined }}
        >
          <BookOpen className="w-3.5 h-3.5" style={{ color: goal.iconColor }} /> {g.peptide} guide
        </Link>
      ))}
    </div>
  );
}

export default function ShopCategory() {
  const { slug } = useParams();
  const goal = goalBySlug(slug);
  const [rawProducts, setRawProducts] = useState([]);
  const [guides, setGuides] = useState([]);
  const [sortBy, setSortBy] = useState("featured");
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    api.get("/products")
      .then(({ data }) => setRawProducts(data))
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setLoading(false));
    api.get("/guides").then(({ data }) => setGuides(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [slug]);

  const families = useMemo(() => groupByName(rawProducts), [rawProducts]);
  const inGoal = useMemo(() => (goal ? [...familiesForGoal(families, goal)].sort(SORTS[sortBy].fn) : []), [families, goal, sortBy]);
  const goalGuides = useMemo(() => {
    const names = new Set(inGoal.map((f) => f.name));
    return guides.filter((g) => names.has(g.peptide));
  }, [guides, inGoal]);
  const otherGoals = useMemo(() => GOALS.filter((g) => g.slug !== slug && families.some((f) => f.category === g.category)), [families, slug]);

  if (!goal) return <Navigate to="/shop" replace />;
  const Icon = goal.icon;

  const handleAdd = (e, product) => {
    e.preventDefault();
    addItem(product);
    toast.success(`${product.name} ${product.dosage} added to cart`);
  };

  return (
    <div className="relative z-10">
      <Seo
        title={`${goal.name} Peptides`}
        path={`/shop/${goal.slug}`}
        description={`${goal.tagline} Browse ${goal.name.toLowerCase()} research peptides at Eternal Biolabs — third-party tested, ships from Canada.`}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${siteOrigin()}/` },
                { "@type": "ListItem", position: 2, name: "Shop All", item: `${siteOrigin()}/shop` },
                { "@type": "ListItem", position: 3, name: goal.name, item: `${siteOrigin()}/shop/${goal.slug}` },
              ],
            },
            {
              "@type": "ItemList", name: `${goal.name} research peptides`,
              itemListElement: inGoal.map((f, i) => ({ "@type": "ListItem", position: i + 1, name: f.name, url: `${siteOrigin()}/product/${f.id}` })),
            },
          ],
        }}
      />

      <section className={`border-b bg-gradient-to-br ${goal.gradient}`} style={{ borderColor: goal.border }} data-testid="category-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 sm:pt-14 pb-10 sm:pb-14">
          <nav className="text-xs tracking-[0.15em] uppercase font-semibold text-[#6E675F] mb-6" data-testid="category-breadcrumb">
            <Link to="/" className="hover:text-[#1A1A1A] transition-colors">Home</Link>
            <span className="mx-2 text-[#1A1A1A]/30">/</span>
            <Link to="/shop" className="hover:text-[#1A1A1A] transition-colors">Shop All</Link>
            <span className="mx-2 text-[#1A1A1A]/30">/</span>
            <span className="text-[#1A1A1A]">{goal.name}</span>
          </nav>
          <div className="flex items-start gap-4 sm:gap-6">
            <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-xl bg-white shadow-[0_10px_30px_-14px_rgba(26,26,26,0.35)] ring-1 ring-black/5 overflow-hidden shrink-0" data-testid="category-hero-image">
              <img src={goal.image} alt={`${goal.name} research vial`} className="w-full h-full object-cover" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="w-9 h-9 rounded-full bg-white/80 backdrop-blur hidden sm:flex items-center justify-center shrink-0" style={{ color: goal.iconColor }}>
                  <Icon className="w-[18px] h-[18px]" />
                </span>
                <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase tracking-wide text-[#1A1A1A]" data-testid="category-heading">{goal.name}</h1>
                <span className="text-[11px] tracking-[0.15em] uppercase font-semibold px-2.5 py-1 rounded-none" style={{ background: goal.badgeBg, color: goal.badgeText }} data-testid="category-count">
                  {inGoal.length} {inGoal.length === 1 ? "product" : "products"}
                </span>
              </div>
              <p className="text-sm sm:text-base text-[#6E675F] mt-3 max-w-2xl leading-relaxed" data-testid="category-tagline">{goal.tagline}</p>
              <GuideLinks guides={goalGuides} goal={goal} />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-8 sm:pt-12 pb-16">
        <div className="flex items-center justify-between gap-4 mb-8 sm:mb-10">
          <Link to="/shop" data-testid="category-back-link" className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase font-semibold text-[#6E675F] hover:text-[#1A1A1A] transition-colors">
            <ArrowLeft className="w-4 h-4" /> All products
          </Link>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs tracking-[0.15em] uppercase font-semibold text-[#6E675F]">Sort</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="category-sort-select" className="w-[170px] sm:w-[190px] rounded-none bg-white border-[#DDD7CF] text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SORTS).map(([k, s]) => (
                  <SelectItem key={k} value={k} data-testid={`category-sort-option-${k}`}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : inGoal.length === 0 ? (
          <p className="text-center text-[#6E675F] py-24" data-testid="category-empty">Nothing listed under {goal.name} right now.</p>
        ) : (
          <ProductGrid families={inGoal} onAdd={handleAdd} testid="category-product-grid" />
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-16" data-testid="category-blurb">
        <div className="rounded-2xl border border-[#DDD7CF] bg-white p-7 sm:p-10 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[#8C7B6B] mb-3">About this research area</p>
            <p className="text-sm sm:text-base text-[#6E675F] leading-relaxed max-w-3xl">{goal.blurb}</p>
            <p className="text-xs text-[#6E675F]/80 mt-4">For laboratory research use only. Not for human or veterinary consumption. No dosing or usage guidance is provided.</p>
          </div>
          <Link to="/research" data-testid="category-research-link" className="inline-flex items-center justify-center gap-2 border border-[#8C7B6B] text-[#8C7B6B] text-xs tracking-[0.15em] uppercase font-semibold px-6 py-3.5 rounded-none hover:bg-[#8C7B6B] hover:text-white transition-colors duration-300 shrink-0">
            Research hub <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {otherGoals.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20" data-testid="category-other-goals">
          <div className="flex items-center gap-3 mb-6 sm:mb-8">
            <h2 className="font-display text-base sm:text-xl uppercase tracking-wide text-[#1A1A1A]">Other research goals</h2>
            <div className="flex-1 h-px bg-[#DDD7CF]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {otherGoals.map((g, i) => <GoalTile key={g.slug} goal={g} families={families} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
